import { pick } from "lodash";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { CREATE_ALERT_CONFIG, UPDATE_ALERT_CONFIG } from "../../../actions";
import { FORM_ERROR } from "final-form";
import { Field, Form } from "react-final-form";
import { NumberInput, PercentageInput, WrappedCheckbox } from "../../../utils";
import { Button, HelpBlock } from "react-bootstrap";
import { makeOptionalAuthRequest } from "../../../http";
import { useAuthErrorHandler } from "../../../hooks";

const UPDATE_FIELDS = [
  "id",
  "symbol",
  "active",
  "minimum_fee",
  "maximum_fee",
  "minimum_available",
  "maximum_available",
];

const FEE_MINIMUM_MESSAGE = "Must be less than 1,000%";
const FEE_SANITY_MESSAGE = "Minimum fee must be less than maximum fee.";
const AVAILABLE_MAXIMUM_MESSAGE = "Must be less than 10,000,000";
const AVAILABLE_SANITY_MESSAGE =
  "Minimum available must be less than maximum available.";

const validateConfig = ({
  minimum_fee,
  maximum_fee,
  minimum_available,
  maximum_available,
}) => {
  const errors = {};
  if (minimum_fee && minimum_fee > 1000) {
    errors.minimum_fee = FEE_MINIMUM_MESSAGE;
  }
  if (maximum_fee && maximum_fee > 1000) {
    errors.maximum_fee = FEE_MINIMUM_MESSAGE;
  }
  if (minimum_fee && maximum_fee && minimum_fee > maximum_fee) {
    errors.minimum_fee = FEE_SANITY_MESSAGE;
    errors.maximum_fee = FEE_SANITY_MESSAGE;
  }
  if (minimum_available && minimum_available > 10000000) {
    errors.minimum_available = AVAILABLE_MAXIMUM_MESSAGE;
  }
  if (maximum_available && maximum_available > 10000000) {
    errors.maximum_available = AVAILABLE_MAXIMUM_MESSAGE;
  }
  if (
    minimum_available &&
    maximum_available &&
    minimum_available > maximum_available
  ) {
    errors.minimum_available = AVAILABLE_SANITY_MESSAGE;
    errors.maximum_available = AVAILABLE_SANITY_MESSAGE;
  }
  return errors;
};

const ConfigForm = ({ config = undefined, onSubmit, loading }) => (
  <Form
    onSubmit={onSubmit}
    initialValues={config}
    validate={validateConfig}
    validateOnBlur
    render={({ handleSubmit, submitError, valid }) => {
      return (
        <form onSubmit={handleSubmit}>
          <Field
            name="minimum_fee"
            component={PercentageInput}
            type="text"
            label="Minimum Fee"
          />
          <Field
            name="maximum_fee"
            component={PercentageInput}
            type="text"
            label="Maximum Fee"
          />
          <Field
            name="minimum_available"
            component={NumberInput}
            type="text"
            label="Minimum Available"
          />
          <Field
            name="maximum_available"
            component={NumberInput}
            type="text"
            label="Maximum Available"
          />
          <Field
            name="active"
            component={WrappedCheckbox}
            label="Active"
            type="checkbox"
          />
          <Button type="submit" disabled={loading || !valid}>
            Submit
          </Button>
          {submitError && <HelpBlock bsStyle="danger">{submitError}</HelpBlock>}
        </form>
      );
    }}
  />
);

export const UpdateConfigForm = ({ config, onComplete }) => {
  const handleError = useAuthErrorHandler();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const onSubmit = useCallback(
    async (updatedConfig) => {
      setLoading(true);
      try {
        const response = await makeOptionalAuthRequest().put(
          `/api/alert_configs/${updatedConfig.id}`,
          { data: pick(updatedConfig, UPDATE_FIELDS) }
        );
        dispatch({
          type: UPDATE_ALERT_CONFIG,
          payload: response.data,
        });
        onComplete();
      } catch (error) {
        setLoading(false);
        handleError(error, onComplete);
        return {
          [FORM_ERROR]: error?.response?.data?.errors
            ?.map((error) => error?.detail)
            .join(", "),
        };
      }
    },
    [setLoading, dispatch, onComplete, UPDATE_FIELDS]
  );

  return <ConfigForm config={config} onSubmit={onSubmit} loading={loading} />;
};

export const CreateConfigForm = ({ symbol, onComplete }) => {
  const handleError = useAuthErrorHandler();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const onSubmit = useCallback(
    async (config) => {
      setLoading(true);
      try {
        const response = await makeOptionalAuthRequest().post(
          "/api/alert_configs",
          { data: { ...config, symbol } }
        );
        dispatch({
          type: CREATE_ALERT_CONFIG,
          payload: response.data,
        });
        onComplete();
      } catch (error) {
        setLoading(false);
        handleError(error, onComplete);
        return {
          [FORM_ERROR]: error?.response?.data?.errors
            ?.map((error) => error?.detail)
            .join(", "),
        };
      }
    },
    [setLoading, onComplete, dispatch]
  );

  return (
    <ConfigForm
      config={{ active: true }}
      onSubmit={onSubmit}
      loading={loading}
    />
  );
};
